'use client';

/**
 * useAttentionInbox — data layer for the header notification bell's
 * "attention" sections: pending @mentions, approvals awaiting action, and
 * managed tasks due today/overdue.
 *
 * This logic previously lived inside ManagedTasksModal's mentions/approvals
 * tabs. It was split out so the notification bell preview panel (and its
 * detail slide-over) can surface the same items without opening the modal.
 */

import { useCallback, useState } from 'react';
import { apiFetch } from '@/lib/apiFetch';
import { useAuth } from '@/hooks/useAuth';
import { useBadge } from '@/contexts/BadgeContext';
import { notify, prompt as promptDialog } from '@/lib/notifications';
import type { WorkItem } from '@/components/UniversalWorkItems';

export const MYHR_LEAVE_APPROVAL_LINK = '/client-zone/dashboard?view=myhr&myhr_tab=leave&myhr_section=workspace';

export type PendingTagNotification = {
  message_id: string;
  thread_id: string;
  thread_name: string;
  sender_name: string;
  sender_avatar?: string;
  timestamp: string;
  message_preview: string;
  source?: 'thread' | 'work_item_comment';
  work_item_id?: string;
};

export type ManagedLeaveApproval = {
  approval_type: 'leave';
  id: number;
  employee_id: number;
  employee_name: string;
  employee_number?: string | null;
  leave_type_id: number;
  leave_type_name: string;
  leave_type_color?: string | null;
  start_date: string;
  end_date: string;
  total_days: number;
  reason: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  requested_at: string;
  current_approver_id?: number | null;
  current_approver_name?: string | null;
  requires_action?: boolean;
};

export type ManagedOvertimeApproval = {
  approval_type: 'overtime';
  id: number;
  employee_id: number;
  employee_name: string;
  employee_number?: string | null;
  overtime_type: string;
  requested_at: string;
  request_date?: string;
  overtime_date?: string;
  planned_start_time?: string;
  planned_end_time?: string;
  planned_hours?: number | string | null;
  total_days?: number;
  start_date?: string;
  end_date?: string;
  leave_type_name?: string;
  leave_type_color?: string | null;
  reason: string;
  status: 'pending' | 'approved' | 'rejected' | 'completed' | 'cancelled';
  request_status?: 'pending' | 'approved_primary' | 'approved_secondary' | 'approved_final' | 'rejected' | 'completed';
  current_approver_id?: number | null;
  current_approver_name?: string | null;
  primary_approver_name?: string | null;
  secondary_approver_name?: string | null;
  final_approver_name?: string | null;
  requires_action?: boolean;
  overtime_session_id?: number | null;
  session_status?: 'pending' | 'active' | 'completed' | 'nullified' | 'rejected';
  cliff_met?: boolean;
  claimed_overtime_minutes?: number;
  actual_overtime_minutes?: number;
  approval_deadline?: string;
};

export type ManagedShiftViolationApproval = {
  approval_type: 'shift_violation';
  id: number;
  employee_id: number;
  employee_name: string;
  employee_number?: string | null;
  violation_type: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  details?: {
    violation_subtype?: 'late_clock_in' | 'early_clock_out';
    minutes_late?: number;
    minutes_early?: number;
    shift_start?: string;
    shift_end?: string;
    platform?: string;
    timestamp?: string;
    [key: string]: unknown;
  } | null;
  reason: string;
  status: 'pending' | 'approved' | 'rejected' | 'resolved';
  requested_at: string;
  requires_action?: boolean;
};

export type ManagedDeviceAdminApproval = {
  approval_type: 'device_admin';
  id: number;
  device_id: number;
  user_id: number;
  device_name: string;
  local_username: string;
  employee_name: string;
  requester_email?: string | null;
  reason?: string | null;
  ttl_minutes: number;
  status: 'pending' | 'approved' | 'rejected' | 'active' | 'expired' | 'revoked' | 'failed';
  requested_at: string;
  requires_action?: boolean;
};

export type ManagedApprovalRequest =
  | ManagedLeaveApproval
  | ManagedOvertimeApproval
  | ManagedShiftViolationApproval
  | ManagedDeviceAdminApproval;

type ManagedApprovalsResponse = {
  success?: boolean;
  data?: {
    requests?: ManagedApprovalRequest[];
    stats?: {
      pending?: number;
    };
  };
  error?: string;
};

/** Mirrors TERMINAL_MANAGED_TASK_STATUSES in api/v1/badge-counts so the
 *  tasks-due list matches the badge count. */
const TERMINAL_TASK_STATUSES = [
  'cancelled',
  'canceled',
  'closed',
  'completed',
  'done',
  'finished',
  'resolved',
];

export function appendDismissedMentionsLocal(threadId: string, messageIds: string[]) {
  if (typeof window === 'undefined' || messageIds.length === 0) return;
  const key = `dismissed_mentions_${threadId}`;
  try {
    const existing: string[] = JSON.parse(window.localStorage.getItem(key) || '[]');
    const merged = [...new Set([...existing, ...messageIds])];
    window.localStorage.setItem(key, JSON.stringify(merged));
  } catch {
    // Ignore local storage failures.
  }
}

export function getSessionStatusColor(status: string): string {
  switch (status) {
    case 'active':
      return '#2563eb';
    case 'completed':
      return '#7c3aed';
    case 'nullified':
      return '#dc2626';
    case 'rejected':
      return '#dc2626';
    case 'pending':
      return '#f59e0b';
    default:
      return '#6b7280';
  }
}

export function getSessionStatusLabel(status: string): string {
  switch (status) {
    case 'active':
      return 'Overtime In Progress';
    case 'completed':
      return 'Session Complete';
    case 'nullified':
      return 'Nullified';
    case 'rejected':
      return 'Rejected';
    case 'pending':
      return 'Session Pending';
    default:
      return status;
  }
}

/** Shared per-request display derivation used by both the bell list rows and
 *  the detail slide-over. */
export function getApprovalDisplay(request: ManagedApprovalRequest, fallbackAccent: string) {
  const isLeaveApproval = request.approval_type === 'leave';
  const isShiftViolation = request.approval_type === 'shift_violation';
  const isDeviceAdmin = request.approval_type === 'device_admin';
  const isOvertimeApproval = !isLeaveApproval && !isShiftViolation && !isDeviceAdmin;

  const requestLabel = isLeaveApproval
    ? request.leave_type_name
    : isShiftViolation
      ? 'Shift Violation'
      : isDeviceAdmin
        ? 'Device Admin'
        : request.overtime_type.replace(/_/g, ' ');

  const accentColor = isLeaveApproval
    ? (request.leave_type_color || fallbackAccent)
    : isShiftViolation
      ? (request.severity === 'critical' ? '#dc2626' : request.severity === 'high' ? '#ea580c' : '#f59e0b')
      : isDeviceAdmin
        ? '#dc2626'
        : '#f59e0b';

  const summary = isLeaveApproval
    ? `Requested ${request.total_days} ${request.total_days === 1 ? 'day' : 'days'} for ${new Date(request.start_date).toLocaleDateString()} ${request.start_date === request.end_date ? '' : `to ${new Date(request.end_date).toLocaleDateString()}`}`
    : isShiftViolation
      ? request.reason || 'Shift timing violation'
      : isDeviceAdmin
        ? `Administrator rights on ${request.device_name} as ${request.local_username} · ${request.ttl_minutes} min`
        : `Requested ${Number(request.planned_hours || 0)} hour${Number(request.planned_hours || 0) === 1 ? '' : 's'} on ${new Date(request.overtime_date || request.request_date || request.requested_at).toLocaleDateString()}${request.planned_start_time && request.planned_end_time ? ` · ${request.planned_start_time} to ${request.planned_end_time}` : ''}`;

  const overtimeRequest = isOvertimeApproval ? request : null;
  const sessionStatus = overtimeRequest?.session_status;
  const sessionStatusColor = sessionStatus ? getSessionStatusColor(sessionStatus) : null;
  const sessionStatusLabel = sessionStatus ? getSessionStatusLabel(sessionStatus) : null;
  const showCliffWarning = overtimeRequest?.session_status === 'completed' && overtimeRequest?.cliff_met === false;
  const showApprovalDeadline = Boolean(
    overtimeRequest?.approval_deadline
    && (overtimeRequest?.session_status === 'active' || overtimeRequest?.session_status === 'completed'),
  );

  return {
    isLeaveApproval,
    isShiftViolation,
    isDeviceAdmin,
    isOvertimeApproval,
    overtimeRequest,
    requestLabel,
    accentColor,
    summary,
    sessionStatus,
    sessionStatusColor,
    sessionStatusLabel,
    showCliffWarning,
    showApprovalDeadline,
  };
}

export function useAttentionInbox() {
  const { user } = useAuth();
  const { refreshBadgeCounts } = useBadge();
  const userId = user?.id;

  const [mentions, setMentions] = useState<PendingTagNotification[]>([]);
  const [mentionsLoading, setMentionsLoading] = useState(false);
  const [approvals, setApprovals] = useState<ManagedApprovalRequest[]>([]);
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [dueTasks, setDueTasks] = useState<WorkItem[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [approvalActionIds, setApprovalActionIds] = useState<Set<number>>(() => new Set());

  const loadMentions = useCallback(async () => {
    try {
      setMentionsLoading(true);
      const response = await apiFetch('/api/v1/client-threads/pending-mentions?limit=120', {
        credentials: 'include',
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result?.success) {
        setMentions([]);
        return;
      }

      setMentions(Array.isArray(result.items) ? (result.items as PendingTagNotification[]) : []);
    } catch (error) {
      console.error('[useAttentionInbox] Failed to load mentions:', error);
      setMentions([]);
    } finally {
      setMentionsLoading(false);
    }
  }, []);

  const loadApprovals = useCallback(async () => {
    try {
      setApprovalsLoading(true);
      const response = await apiFetch('/api/v1/employee-management/actions', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getManagedApprovals' }),
      });
      const result = await response.json().catch(() => ({} as ManagedApprovalsResponse));

      if (!response.ok || !result?.success) {
        setApprovals([]);
        return;
      }

      const requests: ManagedApprovalRequest[] = Array.isArray(result.data?.requests) ? result.data.requests : [];
      setApprovals(requests.filter((request) => request.requires_action));
    } catch (error) {
      console.error('[useAttentionInbox] Failed to load approvals:', error);
      setApprovals([]);
    } finally {
      setApprovalsLoading(false);
    }
  }, []);

  // Managed tasks/subtasks assigned to me that are due today or overdue —
  // same filter as the badge-counts API (junction-table assignees covered by
  // the assigned_to filter server-side).
  const loadDueTasks = useCallback(async () => {
    if (!userId) {
      setDueTasks([]);
      return;
    }
    try {
      setTasksLoading(true);
      const tomorrow = new Date();
      tomorrow.setHours(0, 0, 0, 0);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const params = new URLSearchParams({
        item_type: 'task,subtask',
        assigned_to: String(userId),
        due_before: tomorrow.toISOString(),
        status_not_in: TERMINAL_TASK_STATUSES.join(','),
        sort_by: 'due_date',
        sort_order: 'asc',
        limit: '25',
      });

      const response = await apiFetch(`/api/v1/work-items?${params.toString()}`, {
        credentials: 'include',
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok || result?.success === false) {
        setDueTasks([]);
        return;
      }

      const rows: WorkItem[] = Array.isArray(result?.data)
        ? result.data
        : (Array.isArray(result?.items) ? result.items : []);
      setDueTasks(rows);
    } catch (error) {
      console.error('[useAttentionInbox] Failed to load due tasks:', error);
      setDueTasks([]);
    } finally {
      setTasksLoading(false);
    }
  }, [userId]);

  const refresh = useCallback(async () => {
    await Promise.allSettled([loadMentions(), loadApprovals(), loadDueTasks()]);
  }, [loadApprovals, loadDueTasks, loadMentions]);

  const dismissMentions = useCallback(async (entries: PendingTagNotification[]) => {
    if (entries.length === 0) return;

    const byThread = new Map<string, string[]>();
    for (const entry of entries) {
      const bucket = byThread.get(entry.thread_id) || [];
      bucket.push(entry.message_id);
      byThread.set(entry.thread_id, bucket);
    }

    for (const [thread_id, message_ids] of byThread.entries()) {
      try {
        const response = await apiFetch('/api/v1/client-threads/dismiss-mention', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ thread_id, message_ids }),
        });

        if (response.ok) {
          appendDismissedMentionsLocal(thread_id, message_ids);
        }
      } catch (error) {
        console.error('[useAttentionInbox] Failed to dismiss mention:', error);
      }
    }

    await loadMentions();
    void refreshBadgeCounts();
  }, [loadMentions, refreshBadgeCounts]);

  const setApprovalActionPending = useCallback((requestId: number, pending: boolean) => {
    setApprovalActionIds((current) => {
      const next = new Set(current);
      if (pending) {
        next.add(requestId);
      } else {
        next.delete(requestId);
      }
      return next;
    });
  }, []);

  const submitApprovalAction = useCallback(async ({
    request,
    action,
    payload,
    successMessage,
  }: {
    request: ManagedApprovalRequest;
    action: 'approve' | 'reject';
    payload: Record<string, unknown>;
    successMessage: string;
  }) => {
    setApprovalActionPending(request.id, true);

    try {
      let response: Response;
      if (request.approval_type === 'device_admin') {
        response = await apiFetch(`/api/v1/devices/${request.device_id}/admin-requests/${request.id}/decide`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action, reason: payload.reason ?? null }),
        });
      } else if (request.approval_type === 'shift_violation') {
        response = await apiFetch('/api/v1/time-attendance/dashboard', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'resolveSecurityViolation',
            violation_id: request.id,
            resolution_notes: payload.resolution_notes || (action === 'approve' ? 'Acknowledged by manager' : ''),
          }),
        });
      } else if (request.approval_type === 'leave') {
        response = await apiFetch('/api/v1/employee-management/actions', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: action === 'approve' ? 'approveLeaveRequest' : 'rejectLeaveRequest',
            requestId: request.id,
            ...payload,
          }),
        });
      } else {
        response = await apiFetch('/api/v1/time-attendance/overtime', {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: request.id,
            action,
            approval_level: request.request_status === 'approved_primary'
              ? 'secondary'
              : request.request_status === 'approved_secondary'
                ? 'final'
                : 'primary',
            ...payload,
          }),
        });
      }

      const result = await response.json().catch(() => ({} as { success?: boolean; error?: string }));

      if (!response.ok || !result?.success) {
        throw new Error(result?.error || 'Failed to process approval');
      }

      notify.success(successMessage);
      await Promise.allSettled([
        loadApprovals(),
        refreshBadgeCounts(),
      ]);
    } catch (error) {
      console.error('[useAttentionInbox] Failed to process approval action:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to process approval');
    } finally {
      setApprovalActionPending(request.id, false);
    }
  }, [loadApprovals, refreshBadgeCounts, setApprovalActionPending]);

  const approveRequest = useCallback(async (request: ManagedApprovalRequest) => {
    if (request.approval_type === 'device_admin') {
      await submitApprovalAction({
        request,
        action: 'approve',
        payload: {},
        successMessage: `Granted ${request.employee_name} administrator rights on ${request.device_name} for ${request.ttl_minutes} min`,
      });
      return;
    }
    if (request.approval_type === 'shift_violation') {
      await submitApprovalAction({
        request,
        action: 'approve',
        payload: { violation_id: request.id, resolution: 'acknowledged' },
        successMessage: `Acknowledged ${request.employee_name}'s shift violation`,
      });
      return;
    }
    const label = request.approval_type === 'leave' ? 'leave request' : 'overtime request';
    await submitApprovalAction({
      request,
      action: 'approve',
      payload: request.approval_type === 'leave' ? { notes: '' } : { comments: '' },
      successMessage: `Approved ${request.employee_name}'s ${label}`,
    });
  }, [submitApprovalAction]);

  const rejectRequest = useCallback(async (request: ManagedApprovalRequest) => {
    if (request.approval_type === 'device_admin') {
      const reason = await promptDialog({
        title: 'Reject admin request',
        message: `Provide a reason for rejecting ${request.employee_name}'s administrator request on ${request.device_name}.`,
        placeholder: 'Enter rejection reason',
        confirmText: 'Reject request',
        variant: 'warning',
        required: true,
        validator: (value) => value.trim().length === 0 ? 'A rejection reason is required' : null,
      });
      if (!reason) return;
      await submitApprovalAction({
        request,
        action: 'reject',
        payload: { reason },
        successMessage: `Rejected ${request.employee_name}'s admin request on ${request.device_name}`,
      });
      return;
    }
    if (request.approval_type === 'shift_violation') {
      const reason = await promptDialog({
        title: 'Dismiss Shift Violation',
        message: `Provide a reason for dismissing ${request.employee_name}'s shift violation.`,
        placeholder: 'Enter dismissal reason',
        confirmText: 'Dismiss violation',
        variant: 'warning',
        required: true,
        validator: (value) => value.trim().length === 0 ? 'A dismissal reason is required' : null,
      });

      if (!reason) {
        return;
      }

      await submitApprovalAction({
        request,
        action: 'reject',
        payload: { violation_id: request.id, resolution_notes: reason },
        successMessage: `Dismissed ${request.employee_name}'s shift violation`,
      });
      return;
    }
    const label = request.approval_type === 'leave' ? 'leave request' : 'overtime request';
    const reason = await promptDialog({
      title: `Reject ${label}`,
      message: `Provide a reason for rejecting ${request.employee_name}'s ${label}.`,
      placeholder: 'Enter rejection reason',
      confirmText: 'Reject request',
      variant: 'warning',
      required: true,
      validator: (value) => value.trim().length === 0 ? 'A rejection reason is required' : null,
    });

    if (!reason) {
      return;
    }

    await submitApprovalAction({
      request,
      action: 'reject',
      payload: request.approval_type === 'leave' ? { reason } : { rejection_reason: reason },
      successMessage: `Rejected ${request.employee_name}'s ${label}`,
    });
  }, [submitApprovalAction]);

  return {
    mentions,
    mentionsLoading,
    approvals,
    approvalsLoading,
    dueTasks,
    tasksLoading,
    approvalActionIds,
    refresh,
    loadMentions,
    loadApprovals,
    loadDueTasks,
    dismissMentions,
    approveRequest,
    rejectRequest,
  };
}
